import { useEffect, useRef } from 'react'

interface Props {
  /** HLS .m3u8 playlist URL (video.bsky.app). */
  src: string
  poster?: string
  className?: string
  style?: React.CSSProperties
  autoPlay?: boolean
  loop?: boolean
  muted?: boolean
  controls?: boolean
  /** Defer manifest/segment loading until the user presses play —
   *  for feed lists where eagerly pulling segments for every post
   *  would waste bandwidth. */
  lazy?: boolean
}

/**
 * HLS-capable <video>. Safari plays .m3u8 natively; every other
 * browser gets hls.js (dynamically imported so non-video sessions
 * never pay for it). Bare `<video src=playlist.m3u8>` silently shows
 * only the poster outside Safari — this component is the one place
 * that knows how to actually play bsky video.
 */
export function HlsVideo({ src, poster, className, style, autoPlay, loop, muted, controls, lazy }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Chromium blocks unmuted autoplay outside a user-gesture call
    // stack (the click that opened the lightbox doesn't count by the
    // time React renders). Degrade to muted playback instead of a
    // frozen poster — controls let the user unmute.
    const tryPlay = () => {
      if (!autoPlay) return
      void video.play().catch(() => {
        video.muted = true
        void video.play().catch(() => {})
      })
    }

    let hls: { destroy(): void; startLoad(): void } | undefined
    let cancelled = false
    let onPlay: (() => void) | undefined
    void import('hls.js').then(({ default: Hls }) => {
      if (cancelled) return
      // hls.js wherever MSE exists, native only where it doesn't (iOS
      // Safari). Do NOT gate on canPlayType — Chrome reports "maybe"
      // for m3u8 but its built-in HLS stalls on bsky streams (spinner,
      // no duration), which is how videos silently broke.
      if (!Hls.isSupported()) {
        video.src = src
        tryPlay()
        return
      }
      // Generous timeouts + retries: video.bsky.app playlist responses
      // intermittently take 5-25s (measured), which is the actual
      // outage users see as "videos don't play". hls.js rides it out;
      // native players just die on a spinner.
      const h = new Hls({
        autoStartLoad: !lazy,
        manifestLoadingTimeOut: 20000,
        manifestLoadingMaxRetry: 4,
        levelLoadingTimeOut: 20000,
        levelLoadingMaxRetry: 4,
        fragLoadingTimeOut: 30000,
        fragLoadingMaxRetry: 4,
      })
      hls = h
      h.loadSource(src)
      h.attachMedia(video)
      h.on(Hls.Events.MANIFEST_PARSED, tryPlay)
      if (lazy) {
        onPlay = () => h.startLoad()
        video.addEventListener('play', onPlay, { once: true })
      }
    })
    return () => {
      cancelled = true
      if (onPlay) video.removeEventListener('play', onPlay)
      hls?.destroy()
    }
  }, [src, lazy, autoPlay])

  return (
    <video
      ref={videoRef}
      poster={poster}
      className={className}
      style={style}
      loop={loop}
      muted={muted}
      controls={controls}
      playsInline
      preload={lazy ? 'none' : 'metadata'}
    />
  )
}
